/**
 * UpdateBorrowerContactDialog
 * Allows dealers to update the primary borrower's email and phone after
 * application creation. Uses PUT /borrower/{borId} with Firebase sync handled by backend.
 */

import * as React from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  PhoneInput,
  toast,
} from "@porchpass/ui";
import { putDealerPortalV1DealerIdBorrowerBorId } from "@porchpass/api-client";
import { useDealer } from "src/hooks/useDealer";
import { emailSchema, phoneSchema } from "../utils/validationSchemas";

interface UpdateBorrowerContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentEmail: string;
  currentPhone: string;
  borrowerId: string;
  onSuccess: (email: string, phone: string) => void;
}

export const UpdateBorrowerContactDialog: React.FC<
  UpdateBorrowerContactDialogProps
> = ({
  open,
  onOpenChange,
  currentEmail,
  currentPhone,
  borrowerId,
  onSuccess,
}) => {
  const { dealer_id: dealerId } = useDealer();

  const [email, setEmail] = React.useState(currentEmail);
  const [phone, setPhone] = React.useState(currentPhone);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // Reset form to current values each time the dialog opens.
  React.useEffect(() => {
    if (open) {
      setEmail(currentEmail);
      setPhone(currentPhone);
      setFieldErrors({});
      setSubmitError(null);
    }
  }, [open, currentEmail, currentPhone]);

  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...next } = prev;
        return next;
      });
    }
    if (submitError) setSubmitError(null);
  };

  const handleEmailBlur = () => {
    if (!email) return;
    if (!emailSchema.safeParse(email).success) {
      setFieldErrors((prev) => ({
        ...prev,
        email: "Invalid email address",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});

    // Client-side validation
    const emailValid = emailSchema.safeParse(email).success;
    const phoneValid = phoneSchema.safeParse(phone).success;
    const errors: Record<string, string> = {};

    if (!email.trim()) {
      errors.email = "Email address is required";
    } else if (!emailValid) {
      errors.email = "Invalid email address";
    }

    if (!phone.trim()) {
      errors.phoneNumber = "Phone number is required";
    } else if (!phoneValid) {
      errors.phoneNumber = "Phone number must be exactly 10 digits";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      if (!dealerId) {
        setSubmitError("Dealer ID not found. Please try again.");
        return;
      }
      const resp = await putDealerPortalV1DealerIdBorrowerBorId(
        dealerId,
        borrowerId,
        { email, phone_number: phone },
      );

      if (resp.status === 200) {
        onSuccess(email, phone);
        onOpenChange(false);
        toast.success("Contact information updated");
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } catch (err) {
      const status =
        err && typeof err === "object" && "status" in err
          ? (err as { status: number }).status
          : undefined;
      const message = err instanceof Error ? err.message : "";

      if (status === 400) {
        if (message.includes("email or phone number already in use")) {
          setFieldErrors({
            email: "Email or phone number already in use by another borrower",
            phoneNumber:
              "Email or phone number already in use by another borrower",
          });
        } else if (message.includes("application is no longer editable")) {
          setSubmitError("Application is no longer editable.");
        } else if (message.includes("borrower is not linked")) {
          setSubmitError("Borrower account not found. Please contact support.");
        } else {
          setSubmitError("Something went wrong. Please try again.");
        }
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update contact information</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
            <p className="text-sm text-[#6b7280]">
              Updating email or phone will sync the borrower&apos;s account. A
              new invite link will be sent if the application is pending.
            </p>

            <Input
              label="Email address"
              type="email"
              size="s"
              value={email}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError("email");
              }}
              onBlur={handleEmailBlur}
            />

            <PhoneInput
              label="Phone number"
              size="s"
              value={phone}
              error={!!fieldErrors.phoneNumber}
              helperText={fieldErrors.phoneNumber}
              onChange={(value) => {
                setPhone(value);
                clearFieldError("phoneNumber");
              }}
            />

            {submitError && (
              <p className="text-sm text-[#872525]">{submitError}</p>
            )}

            <DialogFooter className="gap-2 px-0 pt-2 pb-0">
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  size="s"
                  type="button"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                variant="primary"
                size="s"
                loading={isSubmitting}
                disabled={isSubmitting || Object.keys(fieldErrors).length > 0}
              >
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
